import React, { useState, useMemo } from 'react';
import { TimelineShipment } from '@/features/calendar/hooks/useTimelineShipments';
import { Ship, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

// Calendar Component with isolated state
export const CalendarComponent: React.FC<{
  shipmentEvents: TimelineShipment[];
  onShipmentClick: (shipment: TimelineShipment) => void;
}> = ({ shipmentEvents, onShipmentClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return { days };
  }, [currentMonth]);

  const getShipmentsForDate = (date: Date) => {
    if (!date) return { departures: [], arrivals: [] };
    const departures =
      shipmentEvents?.filter((shipment) => shipment.startDate?.toDateString() === date.toDateString()) || [];
    const arrivals =
      shipmentEvents?.filter((shipment) => shipment.endDate?.toDateString() === date.toDateString()) || [];
    return { departures, arrivals };
  };

  const getCarrierColor = (carrier: string, hasEvents: boolean) => {
    const carrierColors: Record<string, string> = {
      Maersk: '#1e40af', // Dark blue - better contrast than original
      MSC: '#d97706', // Dark orange - much better contrast than yellow
      'Hapag-Lloyd': '#3730a3', // Deep indigo - professional, non-error color
      ZIM: '#059669', // Emerald green - good contrast
      'CMA CGM': '#2563eb', // Bright blue - better contrast
      OOCL: '#0d9488', // Teal - calming, non-error color
      COSCO: '#92400e', // Bronze - warm, non-error color
      Evergreen: '#166534', // Dark green - distinguishable from ZIM
      'Yang Ming': '#c2410c', // Dark orange - good contrast
      ONE: '#6b21a8', // Purple - good contrast
      PIL: '#be185d', // Magenta - clearly different from ONE
      'Wan Hai': '#db2777', // Pink - good contrast
    };

    return carrierColors[carrier];
  };

  const ShipmentBox = ({ shipment, type }: { shipment: TimelineShipment; type: 'departure' | 'arrival' }) => {
    const color = getCarrierColor(shipment.carrier, shipment.eventsCount > 0);
    const isToday =
      new Date().toDateString() === (type === 'departure' ? shipment.startDate : shipment.endDate).toDateString();
    const origin = shipment.originLocation || 'Unknown';
    const destination = shipment.destinationLocation || 'Unknown';
    const hasEvents = shipment.eventsCount > 0;

    return (
      <div
        className={`text-xs p-1 mb-1 rounded border-l-2 cursor-pointer hover:shadow-sm transition-shadow group relative ${
          isToday ? 'ring-1 ring-blue-400' : ''
        }`}
        style={{
          backgroundColor: hasEvents ? `${color}20` : `transparent`,
          borderLeftColor: color,
          color: color,
        }}
        onClick={() => onShipmentClick(shipment)}
        title={`${shipment.title}\n${origin} → ${destination}\n${shipment.carrier}\n${
          type === 'departure' ? 'ETD' : 'ETA'
        }: ${(type === 'departure' ? shipment.startDate : shipment.endDate).toLocaleDateString()}`}
      >
        <div className="flex items-center gap-1">
          {type === 'departure' ? <Ship className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
          <span className="font-medium truncate" style={{ color }}>
            {shipment.title}
          </span>
        </div>
        <div className="text-xs opacity-75 truncate">
          {type === 'departure' ? `${origin.split(' ')[0]} → ${destination.split(' ')[0]}` : shipment.carrier}
        </div>
        {shipment.eventsCount > 0 && (
          <div className="text-xs font-medium animate-pulse" style={{ color }}>
            ●
          </div>
        )}

        {/* Hover Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap shadow-lg pointer-events-none">
          <div className="font-semibold">{shipment.title}</div>
          <div className="text-gray-300">{shipment.project?.name}</div>
          <div className="mt-1">
            <div>
              <span className="text-gray-400">Route:</span> {origin} → {destination}
            </div>
            <div>
              <span className="text-gray-400">Carrier:</span> {shipment.carrier}
            </div>
            <div>
              <span className="text-gray-400">ETD:</span> {shipment.startDate.toLocaleDateString()}
            </div>
            <div>
              <span className="text-gray-400">ETA:</span> {shipment.endDate.toLocaleDateString()}
            </div>
            {shipment.eventsCount > 0 && <div className="text-green-400 mt-1">• Has tracking events</div>}
          </div>
          <div className="text-xs text-gray-400 mt-1">Click for details</div>
        </div>
      </div>
    );
  };

  const navigateToToday = () => setCurrentMonth(new Date());

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full overflow-auto">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">
          {currentMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={navigateToToday}
            className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded"
          >
            Today
          </button>
          <button
            onClick={() => setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b">
        {weekDays.map((day) => (
          <div key={day} className="p-3 text-center font-medium text-gray-700 border-r last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {calendarData.days.map((day, index) => {
          const { departures, arrivals } = getShipmentsForDate(day!);
          const isToday = day && day.toDateString() === new Date().toDateString();
          const isCurrentMonth = day && day.getMonth() === currentMonth.getMonth();

          return (
            <div
              key={index}
              className={`min-h-[120px] p-2 border-r border-b last:border-r-0
                                  ${day ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'}
                                  ${isToday ? 'bg-blue-50 border-blue-200' : ''}
                                  ${!isCurrentMonth ? 'text-gray-400' : ''}
                              `}
            >
              {day && (
                <>
                  <div
                    className={`text-sm font-medium mb-2
                                          ${
                                            isToday
                                              ? 'text-blue-600'
                                              : isCurrentMonth
                                              ? 'text-gray-900'
                                              : 'text-gray-400'
                                          }
                                      `}
                  >
                    {day.getDate()}
                    {isToday && <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>}
                  </div>
                  <div className="space-y-1">
                    {departures.map((shipment) => (
                      <ShipmentBox key={`dep-${shipment.id}`} shipment={shipment} type="departure" />
                    ))}
                    {arrivals.map((shipment) => (
                      <ShipmentBox key={`arr-${shipment.id}`} shipment={shipment} type="arrival" />
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
